import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface DerivedTimes {
  latestDeliveryTime: string;
  latestOrderTime: string;
  skiReadyTime: string;
  earliestPickupTime: string;
  earliestReturnTime: string;
}

const DEFAULT_LATEST_DELIVERY = '18:00';
const DEFAULT_EARLIEST_PICKUP = '08:00';

export function normalizeTime(time: string): string {
  if (!time) return '';
  if (/^\d{2}:\d{2}$/.test(time)) return time;
  if (/^\d{1}:\d{2}$/.test(time)) return `0${time}`;
  const hhmmss = time.match(/^(\d{1,2}):(\d{2}):\d{2}$/);
  if (hhmmss) return `${String(hhmmss[1]).padStart(2, '0')}:${hhmmss[2]}`;
  const ampm = time.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (ampm) {
    let hours = parseInt(ampm[1], 10);
    const mins = ampm[2];
    const period = ampm[3].toUpperCase();
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    return `${String(hours).padStart(2, '0')}:${mins}`;
  }
  return time;
}

function isValidTime(time: string): boolean {
  return /^\d{2}:\d{2}$/.test(normalizeTime(time));
}

function subtractMinutes(time: string, minutes: number): string {
  const normalized = normalizeTime(time);
  if (!isValidTime(normalized)) return time;
  const [hours, mins] = normalized.split(':').map(Number);
  const totalMinutes = hours * 60 + mins - minutes;
  const adjusted = totalMinutes < 0 ? totalMinutes + 24 * 60 : totalMinutes;
  return `${String(Math.floor(adjusted / 60) % 24).padStart(2, '0')}:${String(adjusted % 60).padStart(2, '0')}`;
}

function addMinutes(time: string, minutes: number): string {
  const normalized = normalizeTime(time);
  if (!isValidTime(normalized)) return time;
  const [hours, mins] = normalized.split(':').map(Number);
  const totalMinutes = hours * 60 + mins + minutes;
  return `${String(Math.floor(totalMinutes / 60) % 24).padStart(2, '0')}:${String(totalMinutes % 60).padStart(2, '0')}`;
}

function buildDerivedTimes(latestDelivery: string, earliestPickup: string): DerivedTimes {
  return {
    latestDeliveryTime: latestDelivery,
    latestOrderTime: subtractMinutes(latestDelivery, 45),
    skiReadyTime: subtractMinutes(latestDelivery, 30),
    earliestPickupTime: earliestPickup,
    earliestReturnTime: addMinutes(earliestPickup, 30),
  };
}

const defaultTimes = buildDerivedTimes(DEFAULT_LATEST_DELIVERY, DEFAULT_EARLIEST_PICKUP);

// Einfacher In-Memory-Cache damit nicht jede Komponente einzeln lädt
let cachedTimes: DerivedTimes | null = null;
let cacheListeners: Array<(t: DerivedTimes) => void> = [];

async function fetchAndCache(): Promise<DerivedTimes> {
  try {
    const { data, error } = await supabase
      .from('settings')
      .select('latest_delivery_time, earliest_pickup_time')
      .eq('id', 'service_center')
      .maybeSingle();

    if (error || !data) return defaultTimes;

    const latestDelivery = normalizeTime(data.latest_delivery_time) || DEFAULT_LATEST_DELIVERY;
    const earliestPickup = normalizeTime(data.earliest_pickup_time) || DEFAULT_EARLIEST_PICKUP;
    const times = buildDerivedTimes(latestDelivery, earliestPickup);
    cachedTimes = times;
    cacheListeners.forEach((fn) => fn(times));
    return times;
  } catch {
    return defaultTimes;
  }
}

export function invalidateSettingsCache() {
  cachedTimes = null;
  fetchAndCache();
}

export function getHourFromTime(time: string): number {
  const normalized = normalizeTime(time);
  if (!isValidTime(normalized)) return 17;
  return parseInt(normalized.split(':')[0], 10);
}

export function useServiceCenterSettings(): DerivedTimes {
  const [derivedTimes, setDerivedTimes] = useState<DerivedTimes>(cachedTimes ?? defaultTimes);

  useEffect(() => {
    if (cachedTimes) {
      setDerivedTimes(cachedTimes);
    } else {
      fetchAndCache().then(setDerivedTimes);
    }

    const listener = (t: DerivedTimes) => setDerivedTimes(t);
    cacheListeners.push(listener);
    return () => {
      cacheListeners = cacheListeners.filter((l) => l !== listener);
    };
  }, []);

  return derivedTimes;
}

export function useServiceCenterSettingsLive(): DerivedTimes & { refresh: () => void } {
  const [derivedTimes, setDerivedTimes] = useState<DerivedTimes>(cachedTimes ?? defaultTimes);

  const refresh = () => {
    cachedTimes = null;
    fetchAndCache().then(setDerivedTimes);
  };

  useEffect(() => {
    if (cachedTimes) {
      setDerivedTimes(cachedTimes);
    } else {
      fetchAndCache().then(setDerivedTimes);
    }

    const listener = (t: DerivedTimes) => setDerivedTimes(t);
    cacheListeners.push(listener);
    return () => {
      cacheListeners = cacheListeners.filter((l) => l !== listener);
    };
  }, []);

  return { ...derivedTimes, refresh };
}
